/**
 * One business unit, as a card.
 *
 * Carries the x/4 setup indicator, because an unfinished BU is the thing this screen exists to
 * surface — a BU missing its signature step silently blocks the Assinaturas screen, and one
 * missing its flag key silently empties every privileges picker.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { getSetupProgress } from '../../Utils/buSetupSteps';
import type { Tenant } from '../BUPlatformList/BUPlatformList.types';
import { SetupProgress } from '../SetupProgress/SetupProgress';
import { maskEnvironmentKey } from '@/Pages/BUs/WhatsNew/Utils/maskEnvironmentKey';
import { getTenantColor, getTenantIcon } from '@/Shared/brand';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { Tag } from '@/Shared/components/Tag/Tag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  box-shadow: var(--shadow-xs);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const iconBoxStyle = css`
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: var(--radius-m);
  background: var(--color-primary-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const metaStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-m);
  padding: var(--space-m) 0;
  border-top: 1px solid var(--color-divider);
  border-bottom: 1px solid var(--color-divider);
`;

const metaCellStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const metaLabelStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const metaValueStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const keyStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const useCasesStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  margin-top: auto;
`;

export interface BUCardProps {
  tenant: Tenant;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (tenant: Tenant) => void;
  onVersions: (tenant: Tenant) => void;
  onDelete: (tenant: Tenant) => void;
  onRestore: (tenant: Tenant) => void;
}

export const BUCard = ({
  tenant,
  canEdit,
  canDelete,
  onEdit,
  onVersions,
  onDelete,
  onRestore,
}: BUCardProps): JSX.Element => {
  const progress = getSetupProgress(tenant);
  const activeUseCases = (tenant.useCases ?? []).filter(useCase => useCase.isActive);

  return (
    <article id={`bu-card-${tenant.slug}`} className={cardStyle}>
      <div className={headerStyle}>
        <span className={iconBoxStyle}>
          <GLIcon variant={getTenantIcon(tenant)} size={19} fill={getTenantColor(tenant.slug)} />
        </span>
        <div className={identityStyle}>
          <span className={nameStyle}>{tenant.name || tenant.slug}</span>
          <span className={slugStyle}>
            {tenant.slug} · v{tenant.version}
          </span>
        </div>
        <div className={spacerStyle} />
        <StatusTag status={tenant.isActive ? 'success' : 'neutral'} size='sm'>
          {tenant.isActive ? 'Ativa' : 'Excluída'}
        </StatusTag>
      </div>

      <div className={metaStyle}>
        <div className={metaCellStyle}>
          <span className={metaLabelStyle}>Flagsmith env key</span>
          {tenant.flagsmithEnvironmentKey ? (
            <span className={keyStyle}>{maskEnvironmentKey(tenant.flagsmithEnvironmentKey)}</span>
          ) : (
            <span className={keyStyle} style={{ color: 'var(--color-danger)' }}>
              não cadastrada
            </span>
          )}
        </div>
        <div className={metaCellStyle}>
          <span className={metaLabelStyle}>Dashboard</span>
          <span className={metaValueStyle}>{tenant.brand ?? '—'}</span>
        </div>
      </div>

      <div className={metaCellStyle}>
        <span className={metaLabelStyle}>Use cases</span>
        {activeUseCases.length === 0 ? (
          <span className={slugStyle}>nenhum</span>
        ) : (
          <div className={useCasesStyle}>
            {activeUseCases.map(useCase => (
              <Tag key={useCase.id} tone='primary' selected>
                {useCase.name}
              </Tag>
            ))}
          </div>
        )}
      </div>

      <SetupProgress progress={progress} showPending />

      <div className={actionsStyle}>
        {tenant.isActive ? (
          <>
            {canEdit ? (
              <UTButton
                fitContent
                id={`bu-edit-${tenant.slug}`}
                variant='secondary'
                size='small'
                text='Editar'
                icon='Settings.PencilSimple'
                onClick={() => {
                  onEdit(tenant);
                }}
              />
            ) : null}
            {canEdit ? (
              <UTButton
                fitContent
                id={`bu-versions-${tenant.slug}`}
                variant='secondary'
                size='small'
                text='Versões'
                icon='Arrows.CloudArrowUp'
                onClick={() => {
                  onVersions(tenant);
                }}
              />
            ) : null}
            <div className={spacerStyle} />
            {canDelete ? (
              <IconButton
                id={`bu-delete-${tenant.slug}`}
                icon='Misc.Trash'
                size='sm'
                aria-label={`Excluir ${tenant.name || tenant.slug}`}
                onClick={() => {
                  onDelete(tenant);
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <div className={spacerStyle} />
            {canDelete ? (
              <UTButton
                fitContent
                id={`bu-restore-${tenant.slug}`}
                variant='secondary'
                size='small'
                text='Reativar'
                icon='Arrows.ArrowCounterClockwise'
                onClick={() => {
                  onRestore(tenant);
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};
